import { useEffect, useState } from "react";
import styled from "styled-components";
import logo from "./../assets/images/devkey_logo.png";
import { Link, animateScroll as Scroll } from "react-scroll";
import MobileMenu from "./MobileMenu";
import { mobileMaxWidth, colors, fonts, radii, transitions } from "../theme";

const Wrapper = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  z-index: 10;
  pointer-events: none;

  @media screen and (max-width: ${mobileMaxWidth}) {
    padding: 12px 16px;
  }
`;

const Bar = styled.nav<{ activeHeader: boolean }>`
  pointer-events: auto;
  width: 100%;
  max-width: 1160px;
  height: 64px;
  padding: 0 12px 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: ${radii.round};
  border: 1px solid
    ${(props) =>
      props.activeHeader
        ? "rgba(11, 18, 32, 0.06)"
        : "rgba(255, 255, 255, 0.08)"};
  background: ${(props) =>
    props.activeHeader
      ? "rgba(255, 255, 255, 0.78)"
      : "rgba(255, 255, 255, 0.04)"};
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  box-shadow: ${(props) => (props.activeHeader ? colors.shadowMedium : "none")};
  transition: ${transitions.default};
`;

const Logo = styled.img<{ activeHeader: boolean }>`
  height: 28px;
  width: auto;
  filter: ${(props) => (props.activeHeader ? "invert(1)" : "none")};
  cursor: pointer;
  transition: filter 0.3s ease;
`;

const HeaderRow = styled.ul`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0;
  padding: 0;
  list-style: none;

  @media screen and (max-width: ${mobileMaxWidth}) {
    display: none;
  }
`;

const RowItem = styled.span<{ activeHeader: boolean }>`
  display: block;
  padding: 10px 16px;
  border-radius: ${radii.round};
  color: ${(props) =>
    props.activeHeader ? colors.textSecondary : colors.textOnDarkMuted};
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  transition: ${transitions.fast};

  &:hover {
    color: ${(props) => (props.activeHeader ? colors.ink : colors.white)};
    background: ${(props) =>
      props.activeHeader ? colors.surfaceMuted : "rgba(255, 255, 255, 0.08)"};
  }
`;

const Cta = styled(Link)<{ $activeHeader: boolean }>`
  margin-left: 8px;
  padding: 10px 20px;
  border-radius: ${radii.round};
  font-family: ${fonts.body};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  background: ${(props) => (props.$activeHeader ? colors.ink : colors.white)};
  color: ${(props) => (props.$activeHeader ? colors.white : colors.ink)};
  transition: ${transitions.default};

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${colors.shadowMedium};
  }
`;

const Header = () => {
  const [activeHeader, setActiveHeader] = useState(window.scrollY > 60);

  useEffect(() => {
    const scrollListener = () => {
      const isActive = window.scrollY < 60;
      if (isActive && activeHeader) {
        setActiveHeader(false);
      } else if (!isActive && !activeHeader) {
        setActiveHeader(true);
      }
    };

    window.addEventListener("scroll", scrollListener);

    return () => {
      window.removeEventListener("scroll", scrollListener);
    };
  }, [activeHeader]);

  return (
    <Wrapper>
      <Bar activeHeader={activeHeader}>
        <Logo
          src={logo}
          alt="devKey"
          activeHeader={activeHeader}
          onClick={() => Scroll.scrollToTop()}
        />

        <HeaderRow>
          <li>
            <Link to="home" smooth={true} offset={-100} duration={400}>
              <RowItem activeHeader={activeHeader}>Hem</RowItem>
            </Link>
          </li>
          <li>
            <Link to="ourProjects" smooth={true} offset={-100} duration={400}>
              <RowItem activeHeader={activeHeader}>Våra appar</RowItem>
            </Link>
          </li>
          <li>
            <Cta
              to="contact"
              smooth={true}
              offset={-100}
              duration={400}
              $activeHeader={activeHeader}
            >
              Kontakt
            </Cta>
          </li>
        </HeaderRow>
        <MobileMenu activeHeader={activeHeader} />
      </Bar>
    </Wrapper>
  );
};

export default Header;
